import { createRequestHeaders, requester } from '../requester';

import StringConstants from '../constants/StringConstants';

import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { MarketingLead, MarketUpdate } from '../types/Marketing';

const RENTAL_MANAGER_API_V1_PREFIX = 'rental-manager-api/api/v1';

export const client = requester({
  proxyPath: StringConstants.PROXY_PATH,
  api: typeof window === 'undefined' ? process.env.ZG_API : '',
  // logger: getLogger('requester'),
});

const growthClient = requester({
  proxyPath: StringConstants.RG_PROXY_PATH,
  api: typeof window === 'undefined' ? process.env.RG_API : '',
  // logger: getLogger('growthRequester'),
  headers: { 'Content-Type': 'text/plain' },
});

// const parseLogger = getLogger('parseApiResponse');

// TODO: clean up if api calls work on CMS
const requestWrapper = <ResponseType>(
  request: AxiosInstance['get' | 'post' | 'put' | 'delete'],
  endpoint: string,
  config: AxiosRequestConfig = {},
  isRawResponse?: boolean,
) => {
  return async (): Promise<ResponseType> => {
    const requestConfig = {
      ...config,
      headers: {
        ...createRequestHeaders({
          // cookies: state.app.originalServerSideRequestCookies,
          // ip: state.user.ip,
          // userAgent: state.app.userAgent,
          brand: StringConstants.ZILLOW_BRAND,
        }),
        // 'x-csrf-token': state.app.csrfToken,
        ...(typeof window !== 'undefined' && { Accept: 'application/json' }),
      },
    };

    // parseLogger.info(requestConfig, 'request config');

    const response = await request(
      endpoint,
      config.data ? config.data : requestConfig,
      config.data ? requestConfig : undefined,
    );

    // parseLogger.info(
    //   {
    //     status: response.status,
    //     statusText: response.statusText,
    //     data: response.data,
    //   },
    //   'received api response',
    // );

    // if (response.headers['set-cookie']) {
    //   // @ts-ignore
    //   AppActions.updateApp({
    //     apiResponseServerSideRequestCookies: response.headers['set-cookie'],
    //   });
    // }

    if (isRawResponse) {
      return response.data;
    }

    if (response.data.response) {
      return response.data.response;
    }

    return response.data;
  };
};

const api = {
  address: {
    autocomplete: async (partialAddress: string) => {
      return await requestWrapper(client.get, `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/autocomplete`, {
        params: {
          partialAddress,
        },
        timeout: 5000,
      })();
    },
    getListingForAddress: async (fullAddress: string, unit: string) => {
      return await requestWrapper(
        client.get,
        `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/listing/getListingForAddress`,
        {
          params: {
            fullAddress,
            unit,
          },
          timeout: 5000,
        },
      )();
    },
    graphQlListing: async (data: string) => {
      return await requestWrapper(growthClient.post, 'rental-growth-api/api/graphql/v1/listing', {
        data,
      })();
    },
  },
  marketing: {
    createMarketingLead: async (params: MarketingLead) => {
      return await requestWrapper(growthClient.get, 'rental-growth-api/api/v1/marketing/marketingLead/create', {
        params,
      })();
    },
    createMarketUpdate: async (data: MarketUpdate) => {
      return await requestWrapper(growthClient.post, 'rental-growth-api/api/v1/marketing/marketUpdate/create', {
        data: JSON.stringify(data),
      })();
    },
  },
  user: {
    info: async (queryParams: Record<string, unknown>, baseURL = '') =>
      await requestWrapper(client.get, `${baseURL}rental-manager-api/api/web/v2/users/get`, {
        params: queryParams,
      })(),
  },
  users: {
    properties: {
      create: async (data: {
        listingType: string;
        propertyType: string;
        fullAddress: string;
        unitNumber?: string;
        numUnits?: string;
        customerId?: string;
      }) => {
        return await requestWrapper(
          client.post,
          'rental-manager-api/api/web/v1/users/properties/create',
          {
            // requestWrapper has a quirk: without `data`, the AxiosRequestConfig
            // itself is passed as the body and `params` never reach the query
            // string. Pass an empty `data` so axios correctly treats `params`
            // as query string — the Datarouter handler binds fields from the
            // query string (see useCreateProperty in zrmw for the canonical
            // generated-client equivalent).
            data: {},
            params: {
              propertyType: data.propertyType,
              listingType: data.listingType,
              fullAddress: data.fullAddress,
              unitNumber: data.unitNumber,
              numUnits: data.numUnits,
              customerId: data.customerId,
              includeWebAnalytics: true,
            },
            timeout: 5000,
          },
          true,
        )();
      },
    },
  },
  properties: {
    address: {
      details: async (addressData: { address: string; unit: string }) => {
        const params: { fullAddress: string; unit?: string } = { fullAddress: addressData.address };
        if (addressData.unit) {
          params.unit = addressData.unit;
        }

        return await requestWrapper(
          client.get,
          `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/details`,
          {
            params,
            timeout: 5000,
          },
          true,
        )();
      },
      verification: async (addressData: { address: string; unit: string; propertyTypeCode?: number }) => {
        return await requestWrapper(
          client.get,
          `${RENTAL_MANAGER_API_V1_PREFIX}/properties/addresses/verification`,
          {
            params: {
              fullAddress: addressData.address,
              unit: addressData.unit,
              propertyTypeCode: addressData.propertyTypeCode,
            },
            timeout: 5000,
          },
          true,
        )();
      },
    },
    locations: {
      getPublicRestrictions: async (data: {
        address: string;
        unit: string;
        listingTypeCode: number;
        propertyTypeCode: number | null;
        listingAlias?: string;
      }) => {
        return await requestWrapper(
          client.get,
          `${RENTAL_MANAGER_API_V1_PREFIX}/properties/locations/restrictions`,
          {
            params: {
              fullAddress: data.address,
              unit: data.unit,
              listingTypeCode: data.listingTypeCode,
              propertyTypeCode: data.propertyTypeCode,
              listingAlias: data.listingAlias,
            },
            timeout: 5000,
          },
          true,
        )();
      },
      getRestrictions: async (data: {
        address: string;
        unit: string;
        listingTypeCode: number;
        propertyTypeCode: number | null;
        listingAlias?: string;
      }) => {
        return await requestWrapper(
          client.get,
          `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/locations/restrictions`,
          {
            params: {
              fullAddress: data.address,
              unit: data.unit,
              listingTypeCode: data.listingTypeCode,
              propertyTypeCode: data.propertyTypeCode,
              listingAlias: data.listingAlias,
            },
            timeout: 5000,
          },
          true,
        )();
      },
    },
  },
};

export default api;
